import { DropdownOption } from '@tcl-argon-models/models';
import { beforeWrite } from '@popperjs/core';
import { UseComboboxState } from 'downshift';
import { HierarchicalDropdownOption, IHierarchicalDropdownOption } from '.';
import { useState } from 'react';

export const VIEW_ALL_CHILDREN_IDENTIFIER = 'view_all_children_';
const PARENT_OF_IDENTIFIER = '_parent_of_';
const VIEW_ALL_CHILDREN_LABEL = 'View all children';
interface HandleSelectedItemArgs {
  isSingleSelect: boolean;
  newlySelectedItem: DropdownOption;
  alreadySelectedItems: DropdownOption[];
  onChange: (items: DropdownOption[]) => void;
  addSelectedItem: (item: DropdownOption) => void;
  removeSelectedItem: (item: DropdownOption) => void;
  setSelectedItems: (items: DropdownOption[]) => void;
}
interface HandleSelectedItemHierarchicalArgs {
  isSingleSelect: boolean;
  newlySelectedItem: IHierarchicalDropdownOption;
  alreadySelectedItems: IHierarchicalDropdownOption[];
  onChange: (items: IHierarchicalDropdownOption[]) => void;
  addSelectedItem: (item: IHierarchicalDropdownOption) => void;
  removeSelectedItem: (item: IHierarchicalDropdownOption) => void;
  setSelectedItems: (items: IHierarchicalDropdownOption[]) => void;
  flattenedStructure: IHierarchicalDropdownOption[];
  setShowTreeStructure: (showTreeStructure: boolean) => void;
  setParentNode: (parentNode: string) => void;
}
export const handleSelectItem: (args: HandleSelectedItemArgs) => void = ({
  isSingleSelect,
  newlySelectedItem,
  alreadySelectedItems,
  onChange,
  addSelectedItem,
  removeSelectedItem,
  setSelectedItems,
}) => {
  if (isSingleSelect) {
    setSelectedItems([newlySelectedItem]);
    onChange([newlySelectedItem]);
  } else if (alreadySelectedItems.filter((i) => i.value === newlySelectedItem.value).length !== 0) {
    removeSelectedItem(newlySelectedItem);
    onChange(alreadySelectedItems.filter((i) => i.value !== newlySelectedItem.value));
  } else {
    addSelectedItem(newlySelectedItem);
    onChange([...alreadySelectedItems, newlySelectedItem]);
  }
};

export const checkboxInSelectedOptions = (
  selectedOptions: HierarchicalDropdownOption[],
  checkboxValue: string,
): boolean => {
  return selectedOptions.find((x) => x.value === checkboxValue) !== undefined ? true : false;
};

export const propagateSelectionDown = (
  checkboxValue: string,
  check: boolean,
  selectedOptions: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  if (flattenedStructure) {
    const node = flattenedStructure.find((x) => x.value === checkboxValue);
    if (node) {
      if (check) {
        if (!checkboxInSelectedOptions(selectedOptions, checkboxValue)) {
          selectedOptions.push(node);
        }
      } else {
        selectedOptions = selectedOptions.filter((v) => v.value !== checkboxValue);
      }
      if (node && node.children && node.children.length) {
        for (let i = 0; i < node.children.length; i++) {
          selectedOptions = propagateSelectionDown(node.children[i].value, check, selectedOptions, flattenedStructure);
        }
      }
    }
  }
  return selectedOptions;
};

export const checkIsSelected = (checkboxValue: string, flattenedStructure: IHierarchicalDropdownOption[]): boolean => {
  if (flattenedStructure) {
    const checkboxNode = flattenedStructure.find((x) => x.value === checkboxValue);
    return checkboxNode?.status === 'selected' || checkIsIndeterminate(flattenedStructure, checkboxValue);
  }
  return false;
};

export const checkIsIndeterminate = (
  flattenedStructure: IHierarchicalDropdownOption[],
  checkboxValue: string,
): boolean => {
  if (flattenedStructure) {
    const checkboxNode = flattenedStructure.find((x) => x.value === checkboxValue);
    return checkboxNode?.status === 'indeterminate';
  }
  return false;
};

export const propagateSelectionsUp = (
  check: boolean,
  checkboxValue: string,
  selectedOptions: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  let checkedChildCount = 0;
  let childPartialSelected = false;
  if (flattenedStructure) {
    const parentNode = findImmediateParent(flattenedStructure, checkboxValue);
    if (parentNode) {
      parentNode.children?.forEach((child) => {
        const childNode = flattenedStructure.find((x) => x.value === child.value);
        if (checkboxInSelectedOptions(selectedOptions, childNode?.value ?? '')) {
          checkedChildCount++;
        } else if (parentNode.children?.some((v) => checkboxInSelectedOptions(selectedOptions, v.value))) {
          childPartialSelected = true;
        }
      });
      if (check && checkedChildCount === parentNode.children?.length) {
        selectedOptions.push(parentNode);
      } else {
        if (!check) {
          selectedOptions = selectedOptions.filter((v) => v.value !== parentNode.value);
        } else if (
          childPartialSelected ||
          (checkedChildCount > 0 && checkedChildCount !== parentNode.children?.length)
        ) {
          selectedOptions = selectedOptions.filter((v) => v.value !== parentNode.value);
        } else {
          selectedOptions = selectedOptions.filter((v) => v.value !== parentNode.value);
        }
      }
      selectedOptions = propagateSelectionsUp(check, parentNode.value, selectedOptions, flattenedStructure);
    }
  }
  return selectedOptions;
};

export const propagateDown = (
  checkboxValue: string,
  selectedOptions: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  if (flattenedStructure) {
    const checkbox = flattenedStructure.find((x) => x.value === checkboxValue);
    if (checkbox) {
      const check = checkboxInSelectedOptions(selectedOptions, checkbox.value);
      if (check) {
        checkbox.status = 'selected';
      } else {
        checkbox.status = 'unselected';
      }
      if (checkbox.children && checkbox.children.length > 0) {
        checkbox.children.forEach((child) => {
          flattenedStructure = propagateDown(child.value, selectedOptions, flattenedStructure);
        });
      }
    }
  }
  return flattenedStructure;
};

export const propagateUp = (
  check: boolean,
  checkboxValue: string,
  flattenedStructure: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  let checkedChildCount = 0;
  let childPartialSelected = false;
  if (flattenedStructure) {
    const parentNode = findImmediateParent(flattenedStructure, checkboxValue);
    if (parentNode) {
      parentNode.children?.forEach((child) => {
        const childNode = flattenedStructure.find((x) => x.value === child.value);
        if (childNode?.status === 'selected') {
          checkedChildCount++;
        } else if (childNode?.status === 'indeterminate') {
          childPartialSelected = true;
        }
      });
      if (check && checkedChildCount === parentNode.children?.length) {
        parentNode.status = 'selected';
      } else {
        if (childPartialSelected || (checkedChildCount > 0 && checkedChildCount !== parentNode.children?.length)) {
          parentNode.status = 'indeterminate';
        } else {
          parentNode.status = 'unselected';
        }
      }
      flattenedStructure = propagateUp(check, parentNode.value, flattenedStructure);
    }
  }
  return flattenedStructure;
};

export const setExpandedBasedOnClickedValue = (valueClicked: string, items: IHierarchicalDropdownOption[]) => {
  const itemInFlattenedStructure = items.find((item) => item.value === valueClicked);
  if (
    itemInFlattenedStructure &&
    itemInFlattenedStructure.hasParent &&
    (itemInFlattenedStructure.parentItem !== null || itemInFlattenedStructure !== undefined)
  ) {
    const parentItem = items.find((item) => item.value === itemInFlattenedStructure.parentItem.value);
    if (parentItem) {
      parentItem.isExpanded = true;
      setExpandedBasedOnClickedValue(parentItem.value, items);
    }
  }
};

export const resetDefaultExpand = (items: IHierarchicalDropdownOption[]) => {
  items.forEach((item) => {
    item.isExpanded = !item.hasParent || !item.parentItem;
  });
};

export const handleParentLinkSelect = (
  items: IHierarchicalDropdownOption[],
  newlySelectedItem: IHierarchicalDropdownOption,
  setShowTreeStructure: (showTreeStructure: boolean) => void,
  setParentNode: (parentNode: string) => void,
): IHierarchicalDropdownOption => {
  setShowTreeStructure(true);
  setExpandedBasedOnClickedValue(newlySelectedItem.childValue, items);
  if (newlySelectedItem.value.includes(VIEW_ALL_CHILDREN_IDENTIFIER)) {
    const value = newlySelectedItem.value.substr(
      0 + VIEW_ALL_CHILDREN_IDENTIFIER.length,
      newlySelectedItem.value.length,
    );
    setParentNode(value);
    return undefined;
  } else {
    const indexOfParentIndicator = newlySelectedItem.value.indexOf(PARENT_OF_IDENTIFIER);
    setParentNode(newlySelectedItem.value.substr(0, indexOfParentIndicator));
    const childValue = newlySelectedItem.value.substr(
      indexOfParentIndicator + PARENT_OF_IDENTIFIER.length,
      newlySelectedItem.value.length,
    );
    return items.find((x) => x.value === childValue);
  }
};

export const handleSelectItemHierarchical: (args: HandleSelectedItemHierarchicalArgs) => void = ({
  isSingleSelect,
  newlySelectedItem,
  alreadySelectedItems,
  onChange,
  addSelectedItem,
  removeSelectedItem,
  setSelectedItems,
  flattenedStructure,
}) => {
  if (isSingleSelect) {
    setSelectedItems([newlySelectedItem]);
    onChange([newlySelectedItem]);
  } else if (alreadySelectedItems.filter((i) => i.value === newlySelectedItem.value).length !== 0) {
    removeSelectedItem(newlySelectedItem);
    const propogateDownOptions = propagateSelectionDown(
      newlySelectedItem.value,
      false,
      alreadySelectedItems,
      flattenedStructure,
    );
    const propogateDownStructure = propagateDown(newlySelectedItem.value, propogateDownOptions, flattenedStructure);
    const optionsAfterPropogation = propagateSelectionsUp(
      false,
      newlySelectedItem.value,
      propogateDownOptions,
      flattenedStructure,
    );
    const newSelectedOptions = [...new Set([...optionsAfterPropogation])];
    propagateUp(false, newlySelectedItem.value, propogateDownStructure);
    onChange(newSelectedOptions);
  } else {
    addSelectedItem(newlySelectedItem);
    const propogateDownOptions = propagateSelectionDown(
      newlySelectedItem.value,
      true,
      alreadySelectedItems,
      flattenedStructure,
    );
    const propogateDownStructure = propagateDown(newlySelectedItem.value, propogateDownOptions, flattenedStructure);
    const optionsAfterPropogation = propagateSelectionsUp(
      true,
      newlySelectedItem.value,
      propogateDownOptions,
      flattenedStructure,
    );
    const newSelectedOptions = [...new Set([...optionsAfterPropogation])];
    propagateUp(true, newlySelectedItem.value, propogateDownStructure);
    onChange(newSelectedOptions);
  }
};

export const setDefaultData = (hierarchicalData: HierarchicalDropdownOption[]): IHierarchicalDropdownOption[] => {
  const fullItemsData = Array<IHierarchicalDropdownOption>();
  hierarchicalData.forEach((item) => {
    if (item.parentId !== null && item.parentId !== undefined && item.parentId !== '') {
      fullItemsData.push({
        ...item,
        hasParent: true,
        parentItem: hierarchicalData.find((i) => i.value === item.parentId),
      });
    } else {
      fullItemsData.push({
        ...item,
        hasParent: false,
        parentItem: undefined,
      });
    }
  });
  const newItemsArray: IHierarchicalDropdownOption[] = [];
  fullItemsData.forEach((item) => {
    const children = fullItemsData.filter((i) => i.hasParent && i.parentItem.value === item.value);
    const isDefaultExpanded = !item.hasParent || !item.parentItem;
    newItemsArray.push({ ...item, children: children, isExpanded: isDefaultExpanded });
  });
  return newItemsArray;
};

export const getHierarchicalDataForList = (
  list: HierarchicalDropdownOption[],
  hierarchicalData: HierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  const fullItemsData = Array<IHierarchicalDropdownOption>();
  list.forEach((item) => {
    if (item.parentId !== null && item.parentId !== undefined && item.parentId !== '') {
      fullItemsData.push({
        ...item,
        hasParent: true,
        parentItem: hierarchicalData.find((i) => i.value === item.parentId),
      });
    } else {
      fullItemsData.push({
        ...item,
        hasParent: false,
        parentItem: undefined,
      });
    }
  });
  return fullItemsData;
};

// popper modifiers
export const offsetModifier = {
  name: 'offset',
  options: {
    offset: [0, 4],
  },
};

export const sameWidthModifierCreator = ({ menuWidth }: { menuWidth?: string }) => ({
  name: 'sameWidth',
  enabled: true,
  phase: beforeWrite,
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = menuWidth ?? `${state.rects.reference.width}px`;
    // Prevent menuWidth from ever allowing menu to be less wide than input.
    state.styles.popper.minWidth = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = menuWidth ?? `${state.elements.reference.offsetWidth}px`;
  },
});

export const itemToString = (option: DropdownOption) => {
  if (option) {
    return option.label !== null || option.label !== undefined ? option.label : '';
  } else {
    return '';
  }
};
export const itemsToString = (items: DropdownOption[]) => items.map(itemToString).sort().join(', ');

export const itemIsSelected = (item: DropdownOption, selectedItems: DropdownOption[]) =>
  selectedItems.some((selectedItem) => itemToString(selectedItem) === itemToString(item));

export const defaultFilterOption = (inputValue: string, option: DropdownOption): boolean => {
  const parsedInputValue = inputValue.toLowerCase();
  const parsedOptionLabel = option.label.toLowerCase();
  return parsedOptionLabel.includes(parsedInputValue);
};

export const reorderList: (items: DropdownOption[], selectedItems: DropdownOption[]) => DropdownOption[] = (
  items,
  selectedItems,
) => {
  return [...selectedItems, ...items.filter((item) => !itemIsSelected(item, selectedItems))];
};

export const reorderHierarchicalList: (
  items: IHierarchicalDropdownOption[],
  selectedItems: IHierarchicalDropdownOption[],
) => IHierarchicalDropdownOption[] = (items, selectedItems) => {
  return [...selectedItems, ...items.filter((item) => !itemIsSelected(item, selectedItems))];
};

/**
 * Returns Popper strategy based on `escapeClippingArea`. When strategy
 * returned is `undefined`, default Popper strategy will be used.
 */
export const getStrategy = (escapeClippingArea: boolean): 'fixed' | undefined => {
  return escapeClippingArea ? ('fixed' as const) : undefined;
};

/**
 * Downshift and Virtual Scroll don't work perfectly together,
 * so we override Downshift to provide our own logic to enforce that
 * when the last option is highlighted and the keyboard is used
 * to try to navigate further down, that nothing happens and the
 * last option remains highlighted.
 */
export const handleArrowKeyComboBox = ({
  changes,
  dropdownOptions,
  directionDown,
  clearable,
  defaultOptionsLabel,
}: {
  changes: Partial<UseComboboxState<DropdownOption>>;
  dropdownOptions: DropdownOption[];
  directionDown: boolean;
  clearable: boolean;
  defaultOptionsLabel: string;
}): Partial<UseComboboxState<DropdownOption>> => {
  if (dropdownOptions && dropdownOptions[changes.highlightedIndex]) {
    if (
      dropdownOptions[changes.highlightedIndex].value === 'clear' &&
      ((clearable && dropdownOptions[changes.highlightedIndex].label === defaultOptionsLabel) || !clearable)
    ) {
      return {
        ...changes,
        highlightedIndex: directionDown ? changes.highlightedIndex + 1 : dropdownOptions.length - 1,
      };
    } else if (dropdownOptions[changes.highlightedIndex].value === 'divider_and_label') {
      return {
        ...changes,
        highlightedIndex: directionDown ? changes.highlightedIndex + 1 : changes.highlightedIndex - 1,
      };
    }
  }
  return changes;
};

export const handleArrowUpDownComboboxHierarchical = ({
  changes,
  dropdownOptions,
  defaultOptionsLabel,
  directionDown,
}: {
  changes: Partial<UseComboboxState<DropdownOption>>;
  dropdownOptions: IHierarchicalDropdownOption[];
  defaultOptionsLabel: string;
  directionDown: boolean;
}): Partial<UseComboboxState<DropdownOption>> => {
  if (dropdownOptions && dropdownOptions[changes.highlightedIndex].value === 'clear') {
    if (
      dropdownOptions[changes.highlightedIndex].label === 'Results' ||
      dropdownOptions[changes.highlightedIndex].label === defaultOptionsLabel
    ) {
      return {
        ...changes,
        highlightedIndex: directionDown ? changes.highlightedIndex + 1 : dropdownOptions.length - 1,
      };
    }
  } else if (dropdownOptions[changes.highlightedIndex].value === 'divider_and_label') {
    return {
      ...changes,
      highlightedIndex: directionDown ? changes.highlightedIndex + 1 : changes.highlightedIndex - 1,
    };
  }
  return changes;
};

export const isInView = (container: HTMLDivElement, element: Element): boolean => {
  const containerTop = container.offsetTop;
  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top;
  const elementHeight = elementRect.height;

  return elementHeight - (containerTop - elementTop) > 0;
};

export const defaultOptionsIncludesAllSelectedItems = (
  defaultOptions: DropdownOption[] | HierarchicalDropdownOption[],
  selectedItems: DropdownOption[] | HierarchicalDropdownOption[],
): boolean => {
  const doesDefaultOptionsIncludeSelectedOption = (selectedItem: DropdownOption) =>
    defaultOptions.some((op) => op.value === selectedItem.value);
  return selectedItems.every(doesDefaultOptionsIncludeSelectedOption);
};

export const shouldShowDefaultValues = (
  defaultOptions: DropdownOption[] | HierarchicalDropdownOption[],
  hasBlurred: boolean,
  selectedItems: DropdownOption[] | HierarchicalDropdownOption[],
): boolean => {
  if (defaultOptions && defaultOptions.length) {
    return !(
      (hasBlurred && selectedItems.length) ||
      (!hasBlurred && !defaultOptionsIncludesAllSelectedItems(defaultOptions, selectedItems))
    );
  } else {
    return false;
  }
};

export const shouldShowDefaultValuesHierarchical = (
  defaultOptions: IHierarchicalDropdownOption[],
  hasBlurred: boolean,
  selectedItems: IHierarchicalDropdownOption[],
): boolean => {
  if (defaultOptions && defaultOptions.length) {
    return !(
      (hasBlurred && selectedItems.length) ||
      (!hasBlurred && !defaultOptionsIncludesAllSelectedItems(defaultOptions, rollupSelectedItems(selectedItems)))
    );
  } else {
    return false;
  }
};

const findRootParent = (items: IHierarchicalDropdownOption[], childItemValue: string): IHierarchicalDropdownOption => {
  const immediateParent = findImmediateParent(items, childItemValue);
  if (
    immediateParent &&
    immediateParent.hasParent &&
    (immediateParent.parentItem !== null || immediateParent.parentItem !== undefined)
  ) {
    return findRootParent(items, immediateParent.value);
  }
  return immediateParent;
};

const addParentItem = (
  items: IHierarchicalDropdownOption[],
  fullItemsData: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  const newItemsArray: IHierarchicalDropdownOption[] = [];
  items.forEach((item) => {
    newItemsArray.push(item);
    if (item.hasParent) {
      const rootParent = findRootParent(fullItemsData, item.value);
      newItemsArray.push({
        ...rootParent,
        label:
          item.linkLabel !== null && item.linkLabel !== undefined && item.linkLabel !== ''
            ? item.linkLabel
            : rootParent.label,
        value: `${rootParent.value}_parent_of_${item.value}`,
        isParentLink: true,
        childValue: item.value,
      });
    }
    if (item.children && !item.hasParent) {
      newItemsArray.push({
        label:
          item.linkLabel !== null && item.linkLabel !== undefined && item.linkLabel !== ''
            ? item.linkLabel
            : VIEW_ALL_CHILDREN_LABEL,
        value: `${VIEW_ALL_CHILDREN_IDENTIFIER}${item.value}`,
        isParentLink: true,
        hasParent: false,
        parentItem: undefined,
        parentId: undefined,
      });
    }
  });
  return newItemsArray;
};

export const findImmediateParent = (
  checkboxes: IHierarchicalDropdownOption[],
  checkboxValue: string,
): IHierarchicalDropdownOption | undefined => {
  let parentCheckbox: IHierarchicalDropdownOption = {
    label: '',
    value: '',
    parentId: undefined,
    hasParent: false,
    parentItem: null,
  };
  checkboxes.forEach((checkbox) => {
    if (checkbox && checkbox.children && checkbox.children.length) {
      if (checkbox.children.find((x) => x.value === checkboxValue)) {
        parentCheckbox = checkbox;
      }
    }
  });
  return parentCheckbox && parentCheckbox.value ? parentCheckbox : undefined;
};

export const findVisibleParentCheckbox = (
  checkbox: IHierarchicalDropdownOption,
  checkboxes: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption => {
  const immediateParent = findImmediateParent(checkboxes, checkbox.value);
  if (immediateParent && !immediateParent.isExpanded) {
    return immediateParent;
  } else if (immediateParent && immediateParent.isExpanded) {
    return findVisibleParentCheckbox(immediateParent, checkboxes);
  } else {
    return checkbox;
  }
};

export const checkIfCheckboxIsVisible = (checkboxValue: string, items: IHierarchicalDropdownOption[]): boolean => {
  if (items) {
    const checkboxNode = items.find((x) => x.value === checkboxValue);
    const visibleParentCheckbox = findVisibleParentCheckbox(
      checkboxNode ?? {
        label: '',
        value: '',
        children: [],
        hasParent: false,
        parentItem: undefined,
        parentId: undefined,
      },
      items,
    );
    if (visibleParentCheckbox && visibleParentCheckbox.value === checkboxValue) {
      return true;
    }
    if (visibleParentCheckbox && visibleParentCheckbox.children && visibleParentCheckbox.children.length > 0) {
      if (visibleParentCheckbox.isExpanded) {
        return true;
      } else {
        return false;
      }
    } else if (visibleParentCheckbox && visibleParentCheckbox.children && visibleParentCheckbox.children.length === 0) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const getTreeStructure = (
  items: IHierarchicalDropdownOption[],
  parentNodeVal: string,
): IHierarchicalDropdownOption[] => {
  const parentNode = items.find((item) => item.value === parentNodeVal);
  if (parentNode) {
    parentNode.level = 0;
    const treeStructure = [
      { label: 'Back', value: 'back_to_results', hasParent: false, parentItem: undefined, parentId: undefined },
    ].concat(addChildren(items, parentNode));
    addTreeLevel(treeStructure, items);
    return treeStructure;
  }
  return null;
};

const addTreeLevel = (
  treeStructure: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
) => {
  treeStructure.map((item) => {
    const parentNode = flattenedStructure.find((i) => i.value === item.parentId);
    if (parentNode) {
      item.level = parentNode.level + 1;
    } else {
      item.level = 0;
    }
    if (item && item.children && item.children.length > 0) {
      addTreeLevel(item.children, flattenedStructure);
    }
  });
};

const addChildren = (
  items: IHierarchicalDropdownOption[],
  parentValue: IHierarchicalDropdownOption,
): IHierarchicalDropdownOption => {
  const children = items.filter(
    (item) => item.hasParent && item.parentItem && item.parentItem.value === parentValue.value,
  );
  parentValue.children = children;
  children.forEach((child) => {
    addChildren(items, child);
  });
  return parentValue;
};

export const getFlattenedStructure = (
  treeStructure: IHierarchicalDropdownOption[],
  newArray: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  if (treeStructure) {
    treeStructure.forEach((node) => {
      if (checkIfCheckboxIsVisible(node.value, flattenedStructure)) {
        newArray.push(node);
        if (node.children) {
          return getFlattenedStructure(node.children, newArray, flattenedStructure);
        }
      }
    });
  }
  return newArray;
};

export const checkItemsState = (items: IHierarchicalDropdownOption[], selectedItems: IHierarchicalDropdownOption[]) => {
  selectedItems.forEach((item) => {
    const propogateDownOptions = propagateSelectionDown(item.value, true, selectedItems, items);
    const propogateDownStructure = propagateDown(item.value, propogateDownOptions, items);
    propagateUp(true, item.value, propogateDownStructure);
  });
};

export const rollupSelectedItems = (selectedItems: IHierarchicalDropdownOption[]): IHierarchicalDropdownOption[] => {
  const doesSelectedOptionIncludeParentItem = (selectedItem: IHierarchicalDropdownOption) =>
    selectedItems.some(
      (op) => selectedItem.hasParent && selectedItem.parentItem && op.value === selectedItem.parentItem.value,
    );
  return selectedItems.filter((item) => !doesSelectedOptionIncludeParentItem(item));
};

const getDividerAndLabelIndex = (visibleSelectedItems: IHierarchicalDropdownOption[]): number => {
  let index = 1;
  visibleSelectedItems.forEach((item) => {
    index += 1;
    if (item.hasParent || (item.children && !item.hasParent)) {
      index += 1;
    }
  });
  return index;
};

export const getMultiSelectHierarchicalTypeaheadItems = (
  defaultOptions: IHierarchicalDropdownOption[],
  shouldShowDefaultValues: boolean,
  selectedItems: IHierarchicalDropdownOption[],
  items: IHierarchicalDropdownOption[],
  inputValue: string,
  searchResults: IHierarchicalDropdownOption[],
  defaultOptionsLabel: string,
): IHierarchicalDropdownOption[] => {
  if (shouldShowDefaultValues) {
    if (selectedItems.length && !inputValue) {
      const visibleSelectedItems = rollupSelectedItems(selectedItems);
      const newItemsArray = addParentItem(
        [{ label: 'Selected', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined }].concat(
          reorderHierarchicalList(
            getDataFromHierarchicalData(defaultOptions, items),
            getDataFromHierarchicalData(visibleSelectedItems, items),
          ),
        ),
        items,
      );
      if (getDividerAndLabelIndex(visibleSelectedItems) !== newItemsArray.length) {
        newItemsArray.splice(getDividerAndLabelIndex(visibleSelectedItems), 0, {
          label: defaultOptionsLabel,
          value: 'divider_and_label',
          hasParent: false,
          parentItem: undefined,
          parentId: undefined,
        });
      }
      return newItemsArray;
    } else if (defaultOptions && selectedItems.length === 0 && !inputValue) {
      return [
        { label: defaultOptionsLabel, value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(defaultOptions, items), items));
    }
    if (searchResults && searchResults.length > 0) {
      return [
        { label: 'Results', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(searchResults, items), items));
    } else {
      return [];
    }
  } else {
    if (selectedItems.length && !inputValue) {
      return addParentItem(
        [{ label: 'Selected', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined }].concat(
          getDataFromHierarchicalData(rollupSelectedItems(selectedItems), items),
        ),
        items,
      );
    }
    if (searchResults && searchResults.length > 0) {
      return [
        { label: 'Results', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(searchResults, items), items));
    } else {
      return [];
    }
  }
};

export const getSingleSelectHierarchicalTypeaheadItems = (
  defaultOptions: IHierarchicalDropdownOption[],
  shouldShowDefaultValues: boolean,
  selectedItems: IHierarchicalDropdownOption[],
  items: IHierarchicalDropdownOption[],
  inputValue: string,
  searchResults: IHierarchicalDropdownOption[],
  defaultOptionsLabel: string,
): IHierarchicalDropdownOption[] => {
  if (shouldShowDefaultValues) {
    if (defaultOptions && selectedItems.length === 0 && !inputValue) {
      return [
        { label: defaultOptionsLabel, value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(defaultOptions, items), items));
    }
    if (searchResults && searchResults.length > 0) {
      return [
        { label: 'Results', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(searchResults, items), items));
    } else {
      return [];
    }
  } else {
    if (selectedItems.length && !inputValue) {
      return addParentItem(
        [{ label: 'None', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined }].concat(
          getDataFromHierarchicalData(rollupSelectedItems(selectedItems), items),
        ),
        items,
      );
    }
    if (searchResults && searchResults.length > 0) {
      return [
        { label: 'Results', value: 'clear', hasParent: false, parentItem: undefined, parentId: undefined },
      ].concat(addParentItem(getDataFromHierarchicalData(searchResults, items), items));
    } else {
      return [];
    }
  }
};

export const getMultiSelectTypeaheadItems = (
  defaultOptions: DropdownOption[],
  shouldShowDefaultValues: boolean,
  selectedItems: DropdownOption[],
  items: DropdownOption[],
  inputValue: string,
  defaultOptionsLabel: string,
): DropdownOption[] => {
  if (shouldShowDefaultValues) {
    if (selectedItems.length && items.length === 0 && !inputValue) {
      const newItemsArray = [{ label: 'Selected', value: 'clear' }].concat(reorderList(defaultOptions, selectedItems));
      if (selectedItems.length + 1 !== newItemsArray.length) {
        newItemsArray.splice(selectedItems.length + 1, 0, {
          label: defaultOptionsLabel,
          value: 'divider_and_label',
        });
      }
      return newItemsArray;
    } else if (defaultOptions && selectedItems.length === 0 && items.length === 0 && !inputValue) {
      return [{ label: defaultOptionsLabel, value: 'clear' }].concat(defaultOptions);
    }
    return items;
  } else {
    if (selectedItems.length && items.length === 0 && !inputValue) {
      return [{ label: 'Selected', value: 'clear' }].concat(selectedItems);
    }
    return items;
  }
};

export const getSingleSelectTypeaheadItems = (
  defaultOptions: DropdownOption[],
  shouldShowDefaultValues: boolean,
  selectedItems: DropdownOption[],
  items: DropdownOption[],
  inputValue: string,
  defaultOptionsLabel: string,
  clearable: boolean,
): DropdownOption[] => {
  if (shouldShowDefaultValues) {
    if (defaultOptions && selectedItems.length === 0 && items.length === 0 && !inputValue) {
      return [{ label: defaultOptionsLabel, value: 'clear' }].concat(defaultOptions);
    }
    return items;
  } else {
    if (selectedItems.length && items.length === 0 && !inputValue && clearable) {
      return [{ label: 'None', value: 'clear' }].concat(selectedItems);
    } else if (selectedItems.length && items.length === 0 && !inputValue && !clearable) {
      return selectedItems;
    }
    return items;
  }
};

export const useForceUpdate = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update the state to force render
};

export const checkCheckboxState = (
  selectedOptions: IHierarchicalDropdownOption[],
  flattenedStructure: IHierarchicalDropdownOption[],
): { updatedStructure: IHierarchicalDropdownOption[]; updatedSelectedOptions: IHierarchicalDropdownOption[] } => {
  let updatedOptions: IHierarchicalDropdownOption[] = selectedOptions;
  let updatedStructure: IHierarchicalDropdownOption[] = flattenedStructure;
  selectedOptions.forEach((checkbox) => {
    const propogateDownOptions = propagateSelectionDown(checkbox.value, true, selectedOptions, flattenedStructure);
    const propogateDownStructure = propagateDown(checkbox.value, propogateDownOptions, flattenedStructure);
    updatedOptions = propagateSelectionsUp(true, checkbox.value, propogateDownOptions, propogateDownStructure);
    updatedStructure = propagateUp(true, checkbox.value, propogateDownStructure);
  });
  return { updatedSelectedOptions: updatedOptions, updatedStructure: updatedStructure };
};

export const getDataFromHierarchicalData = (
  data: IHierarchicalDropdownOption[],
  hierarchicalData: IHierarchicalDropdownOption[],
): IHierarchicalDropdownOption[] => {
  const newDefaultOptionsArray: Array<IHierarchicalDropdownOption> = [];
  if (data && data.length > 0) {
    data.forEach((option) => {
      const fullDataOption = hierarchicalData.find((item) => item.value === option.value);
      if (fullDataOption) {
        newDefaultOptionsArray.push(fullDataOption);
      }
    });
  }
  return newDefaultOptionsArray;
};

export const getDropdownOptions = (
  items: IHierarchicalDropdownOption[],
  parentNode: string,
  showTreeStructure: boolean,
  isSingleSelect: boolean,
  defaultOptions: IHierarchicalDropdownOption[],
  selectedItems: IHierarchicalDropdownOption[],
  inputValue: string,
  hasBlurred: boolean,
  searchResults: IHierarchicalDropdownOption[],
  defaultOptionsLabel: string,
): IHierarchicalDropdownOption[] => {
  const treeStructure = getTreeStructure(items, parentNode);
  let flattenedStructureBasedOnTreeStructure: IHierarchicalDropdownOption[];
  if (items && treeStructure) {
    flattenedStructureBasedOnTreeStructure = getFlattenedStructure(treeStructure, [], items);
  }
  const newDropdownOptions = showTreeStructure
    ? flattenedStructureBasedOnTreeStructure
    : isSingleSelect
    ? getSingleSelectHierarchicalTypeaheadItems(
        defaultOptions,
        shouldShowDefaultValuesHierarchical(defaultOptions, hasBlurred, selectedItems),
        selectedItems,
        items,
        inputValue,
        searchResults,
        defaultOptionsLabel,
      )
    : getMultiSelectHierarchicalTypeaheadItems(
        defaultOptions,
        shouldShowDefaultValuesHierarchical(defaultOptions, hasBlurred, selectedItems),
        selectedItems,
        items,
        inputValue,
        searchResults,
        defaultOptionsLabel,
      );
  return newDropdownOptions;
};

export const getNewDropdownOptionsArray = (
  selectedItems: DropdownOption[],
  items: DropdownOption[],
): DropdownOption[] => {
  const newItemsArray = [{ label: 'Selected', value: 'clear' }].concat(reorderList(items, selectedItems));
  if (selectedItems.length + 1 !== newItemsArray.length) {
    newItemsArray.splice(selectedItems.length + 1, 0, {
      label: 'Divider',
      value: 'divider_and_label',
    });
  }
  return newItemsArray;
};
